'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { GameEngine, GameState, GameConfig } from '../utils/gameEngine'

export interface UseGameEngineProps {
  config?: Partial<GameConfig>
  playerStartX?: number
  targetStartX?: number
  accelerationKey?: string
  jumpKey?: string
  onGameWon?: (score: number) => void
  onGameOver?: () => void
  mobileAccelerating?: boolean // Ajout pour mobile
}

export const useGameEngine = ({
  config = {},
  playerStartX = 10,
  targetStartX = 200,
  accelerationKey = 'ArrowRight',
  jumpKey = 'Space',
  onGameWon,
  onGameOver,
  mobileAccelerating = false // Ajout pour mobile
}: UseGameEngineProps) => {

// États du jeu
const [gameStarted, setGameStarted] = useState(false)
const [gameOver, setGameOver] = useState(false)
const [gameWon, setGameWon] = useState(false)
const [score, setScore] = useState(0)
const [isRunning, setIsRunning] = useState(false)

// Refs pour éviter les recréations
const gameEngine = useRef(new GameEngine(config))
const gameStateRef = useRef<GameState>(gameEngine.current.createInitialState(playerStartX, targetStartX))
const animationFrameRef = useRef<number>()
const keysPressed = useRef<Set<string>>(new Set())
const lastSpawnTime = useRef({ obstacles: 0, hearts: 0, coins: 0 })

// Gestionnaire de touches
useEffect(() => {
	const handleKeyDown = (e: KeyboardEvent) => {
	keysPressed.current.add(e.code)
	if (e.code === jumpKey && gameStarted && !gameOver && !gameWon) {
		e.preventDefault()
		gameStateRef.current.player.jumping = true
	}
	}

	const handleKeyUp = (e: KeyboardEvent) => {
	keysPressed.current.delete(e.code)
	}

	window.addEventListener('keydown', handleKeyDown)
	window.addEventListener('keyup', handleKeyUp)

	return () => {
	window.removeEventListener('keydown', handleKeyDown)
	window.removeEventListener('keyup', handleKeyUp)
	}
}, [gameStarted, gameOver, gameWon, jumpKey])

// Fonction de saut (pour les clics)
const jump = useCallback(() => {
	if (gameStarted && !gameOver && !gameWon) {
	gameStateRef.current.player.jumping = true
	}
}, [gameStarted, gameOver, gameWon])

// Fonction de rendu (à appeler depuis le composant)
const drawCallback = useRef<(() => void) | null>(null)

const setDrawCallback = useCallback((callback: () => void) => {
	drawCallback.current = callback
}, [])

// Boucle de jeu principale avec rendu intégré
const gameLoop = useCallback((canvasWidth: number) => {
	if (gameOver || gameWon) {
	if (animationFrameRef.current) {
		cancelAnimationFrame(animationFrameRef.current)
		animationFrameRef.current = undefined
	}
	return
	}

	const state = gameStateRef.current
	const now = Date.now()

	// Si l'animation de câlin est active
	if (state.hugAnimation.active) {
	const isComplete = gameEngine.current.updateHugAnimation(state)
	gameEngine.current.updateCollectEffects(state)

	if (isComplete) {
		state.score += gameEngine.current['config'].victoryBonus
		setScore(state.score)
		setGameWon(true)
		onGameWon?.(state.score)
		return
	}
	} else {
	// Mise à jour de l'état isRunning
	const currentIsRunning = keysPressed.current.has(accelerationKey) || mobileAccelerating
	setIsRunning(currentIsRunning)

	// Mise à jour du joueur
	const isAccelerating = keysPressed.current.has(accelerationKey) || mobileAccelerating
	gameEngine.current.updatePlayer(state, keysPressed.current, isAccelerating)

	// Mise à jour de la cible
	gameEngine.current.updateTarget(state)

	// Mise à jour de la caméra
	gameEngine.current.updateCamera(state, canvasWidth)

	// Mise à jour des obstacles
	gameEngine.current.updateObstacles(state, now, lastSpawnTime.current)

	// Mise à jour des collectibles
	gameEngine.current.updateCollectibles(state, now, lastSpawnTime.current)

	// Vérification des collisions avec les obstacles
	if (gameEngine.current.checkObstacleCollisions(state)) {
		setGameOver(true)
		onGameOver?.()
		return
	}

	// Collecte des collectibles
	const scoreGained = gameEngine.current.checkCollectibleCollections(state)
	if (scoreGained > 0) {
		state.score += scoreGained
		setScore(state.score)
	}

	// Mise à jour des effets visuels
	gameEngine.current.updateCollectEffects(state)

	// Calcul de la distance
	state.distance = gameEngine.current.calculateDistance(state)

	// Vérification de la condition de victoire
	if (gameEngine.current.checkWinCondition(state)) {
		gameEngine.current.startHugAnimation(state)
	}

	// Vérification de game over
	if (gameEngine.current.checkGameOverCondition(state, canvasWidth)) {
		setGameOver(true)
		onGameOver?.()
		return
	}
	}

	// Rendu
	if (drawCallback.current) {
	drawCallback.current()
	}

	// Continuer la boucle
	animationFrameRef.current = requestAnimationFrame(() => gameLoop(canvasWidth))
}, [gameOver, gameWon, accelerationKey, onGameWon, onGameOver])

// Fonction pour démarrer le jeu
const startGame = useCallback(() => {
	// Arrêter toute boucle existante
	if (animationFrameRef.current) {
	cancelAnimationFrame(animationFrameRef.current)
	animationFrameRef.current = undefined
	}

	// Réinitialiser l'état du jeu
	gameStateRef.current = gameEngine.current.createInitialState(playerStartX, targetStartX)
	lastSpawnTime.current = { obstacles: 0, hearts: 0, coins: 0 }
	keysPressed.current.clear()

	setScore(0)
	setGameStarted(true)
	setGameOver(false)
	setGameWon(false)
}, [playerStartX, targetStartX])

// Fonction pour démarrer la boucle de jeu (à appeler depuis le composant avec la largeur du canvas)
const startGameLoop = useCallback((canvasWidth: number) => {
	if (gameStarted && !gameOver && !gameWon && !animationFrameRef.current) {
	animationFrameRef.current = requestAnimationFrame(() => gameLoop(canvasWidth))
	}
}, [gameStarted, gameOver, gameWon, gameLoop])

// Gestionnaire automatique de la boucle de jeu
const canvasWidthRef = useRef<number>(800) // Valeur par défaut

const updateCanvasWidth = useCallback((width: number) => {
	canvasWidthRef.current = width
}, [])

// Démarrer automatiquement la boucle quand le jeu commence
useEffect(() => {
	if (gameStarted && !gameOver && !gameWon) {
	const startLoop = () => {
		if (!animationFrameRef.current) {
		animationFrameRef.current = requestAnimationFrame(() => gameLoop(canvasWidthRef.current))
		}
	}
	startLoop()
	}
}, [gameStarted, gameOver, gameWon, gameLoop])

// Arrêter la boucle quand le jeu se termine
useEffect(() => {
	if (gameOver || gameWon) {
	if (animationFrameRef.current) {
		cancelAnimationFrame(animationFrameRef.current)
		animationFrameRef.current = undefined
	}
	}
}, [gameOver, gameWon])

// Cleanup des animations au démontage
useEffect(() => {
	return () => {
	if (animationFrameRef.current) {
		cancelAnimationFrame(animationFrameRef.current)
	}
	}
}, [])

return {
	// États
	gameStarted,
	gameOver,
	gameWon,
	score,

	// État du jeu (pour les fonctions de dessin)
	gameState: gameStateRef.current,

	// Fonctions de contrôle
	startGame,
	startGameLoop,
	jump,
	updateCanvasWidth,
	setDrawCallback,

	// Utilitaires
	isRunning,
	keysPressed: keysPressed.current
}
}
